import React, { useState, useEffect } from 'react';
import { Provider, useSelector } from 'react-redux';
import { compose } from 'redux';
import { useParams } from 'react-router-dom';
import { number } from 'prop-types';
import { useHistory } from 'react-router-dom';

import store from '../../store';
import {
  supportRequestsRestHooks,
  selectEndate,
  selectStartdate,
  selectAuth0Id,
} from '../../store/SupportRequests/SupportRequestsSlice';
import FormHeader from '../../components/FormHeader';
import Button from '../../components/Button';
import appRoutes from '../../routes/appRoutes';
import SupportRequestPresenter from '../../presenters/SupportRequestPresenter';
import { OPEN } from '../../presenters/SupportRequestPresenter';
import useSupportRequests from '../../hooks/useSupportRequests';

import SupportRequest from './components/SupportRequest';
import EditRequestModal from './components/EditRequestModal';

const UPDATE = 'update';
const CLOSE = 'close';
const PENDING = 'pending';

const SupportRequestContainer = () => {
  const history = useHistory();
  const endDate = useSelector(selectEndate);
  const startDate = useSelector(selectStartdate);
  const auth0Id = useSelector(selectAuth0Id);
  const { supportRequestId } = useParams();
  const { updateAndReloadRequest } = useSupportRequests();
  const [page, setPage] = useState(1);
  const params = {
    byState: OPEN,
    createdAfter: startDate,
    createdBefore: endDate,
    byUser: auth0Id,
  };

  const [searchParams, setSearchParams] = useState({
    q: params,
    page: page,
  });

  const [numberOfRequests, setNumberOfRequests] = useState(0);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [formType, setFormType] = useState(CLOSE);

  const handleModalOpen = () => setIsModalOpen(true);
  const handleModalClose = () => setIsModalOpen(false);

  const handleEditRequestClick = () => {
    setFormType(UPDATE);
    handleModalOpen();
  };

  const handleCloseButtonClick = () => {
    setFormType(CLOSE);
    handleModalOpen();
  };

  const handlePendingButtonClick = async () => {
    setFormType(PENDING);
    const params = {
      id: SupportRequestPresenter.id(item),
      supportRequest: { state: 'pending' },
    };

    await handleRequestUpdate(params);
  };

  const handleNextSupportRequestClick = () => {
    const index = items
      .map((object) => object.id)
      .indexOf(Number(supportRequestId));
    if (index < numberOfRequests) {
      history.push(appRoutes.supportRequestPath(items[index + 1].id));
      loadSupportRequest(items[index + 1].id);
    } else {
      setPage(page + 1);
      setSearchParams({ q: params, page: page });
      loadSupportRequests(searchParams);
    }
  };

  const handleRequestUpdate = (params) => {
    return updateAndReloadRequest(params);
  };

  const {
    loadSupportRequest,
    loadResetSupportRequest,
    supportRequest: { loadingStatus, item },
    loadSupportRequests,
    loadResetSupportRequests,
    supportRequests: { items },
  } = supportRequestsRestHooks.use();

  useEffect(() => {
    loadSupportRequest(supportRequestId);
    loadSupportRequests(searchParams);
    return () => {
      loadResetSupportRequest();
      loadResetSupportRequests();
    };
  }, []);

  useEffect(() => {
    setNumberOfRequests(items.length - 1);
  }, [items]);

  const isRequestOpen = SupportRequestPresenter.isOpen(item);
  const isRequestPending = SupportRequestPresenter.isPending(item);

  return (
    <div>
      <div className="support-requests">
        <FormHeader title="Support Request">
          <div className="support-request-buttons">
            {isRequestPending && (
              <Button onClick={handleCloseButtonClick}>Close Request</Button>
            )}
            {isRequestOpen && (
              <Button onClick={handlePendingButtonClick}>
                Set Pending Request
              </Button>
            )}
            {numberOfRequests !== -1 && (
              <div className="support-request-button">
                <Button onClick={handleNextSupportRequestClick}>
                  Next Open Support Request
                </Button>
              </div>
            )}
          </div>
        </FormHeader>
        <SupportRequest
          request={item}
          loadingStatus={loadingStatus}
          onEditModalOpen={handleEditRequestClick}
        />
      </div>
      <EditRequestModal
        request={item}
        isOpen={isModalOpen}
        onClose={handleModalClose}
        onRequestUpdate={handleRequestUpdate}
        formType={formType}
        updateFormType={UPDATE}
        closeFormType={CLOSE}
      />
    </div>
  );
};

const addStore = (ProxyComponent) => (props) =>
  (
    <Provider store={store}>
      <ProxyComponent {...props} />
    </Provider>
  );

export default compose(addStore)(SupportRequestContainer);
